import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  FileText,
  Image as ImageIcon,
  FileAudio,
  FileVideo,
  File as FileIcon,
  Loader2,
  Search,
} from "lucide-react";
import { filesApi } from "../lib/api";

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const iconFor = (ctype = "", name = "") => {
  const c = (ctype || "").toLowerCase();
  if (c.startsWith("image/")) return ImageIcon;
  if (c.startsWith("audio/")) return FileAudio;
  if (c.startsWith("video/")) return FileVideo;
  if (
    c === "application/pdf" ||
    c.startsWith("text/") ||
    /\.(md|txt|json|csv|log|xml|html?)$/i.test(name)
  )
    return FileText;
  return FileIcon;
};

/**
 * Compact modal file picker. Shows the user's Files library and returns the
 * chosen file to `onPick(file)`. Passing `filter="image"` restricts the list
 * to image files (used by Studio for photo manipulation).
 */
export const FilePicker = ({
  open,
  onOpenChange,
  onPick,
  filter = null, // "image" | null
  title = "Attach a file",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    filesApi
      .list()
      .then((res) => {
        if (cancelled) return;
        let files = res.files || [];
        if (filter === "image") {
          files = files.filter((f) =>
            (f.content_type || "").toLowerCase().startsWith("image/")
          );
        }
        setItems(files);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(
          e?.response?.data?.detail || e.message || "Couldn't load files"
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, filter]);

  const filtered = q.trim()
    ? items.filter((f) =>
        f.filename.toLowerCase().includes(q.trim().toLowerCase())
      )
    : items;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="file-picker"
        className="glass-strong border-white/10 w-[95vw] max-w-lg max-h-[80vh] overflow-y-auto overflow-x-hidden p-4 sm:p-5 grid-cols-[minmax(0,1fr)]"
      >
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <p className="text-[11px] text-white/50 -mt-1">
            {filter === "image"
              ? "Pick a photo from your Files library."
              : "Pick a file from your Files library to attach."}
          </p>
        </DialogHeader>

        <div className="relative mt-3">
          <Search className="h-3.5 w-3.5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="file-picker-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full h-9 pl-8 pr-3 bg-black/30 border border-white/10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          />
        </div>

        <div className="mt-3 space-y-1 min-h-[100px]">
          {loading ? (
            <div className="py-8 text-center text-white/50 text-sm flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-white/50 text-sm">
              {items.length
                ? "No files match."
                : filter === "image"
                ? "You haven't uploaded any images yet. Add one in the Files panel."
                : "Your Files library is empty. Upload a file first."}
            </div>
          ) : (
            filtered.map((f) => {
              const Icon = iconFor(f.content_type, f.filename);
              const isImage = (f.content_type || "").startsWith("image/");
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`file-picker-item-${f.id}`}
                  onClick={() => {
                    onPick?.(f);
                    onOpenChange?.(false);
                  }}
                  className="w-full text-left flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-blue-400/40 px-3 py-2 transition-colors"
                >
                  {isImage ? (
                    <div className="h-10 w-10 rounded-md overflow-hidden bg-black/40 shrink-0 border border-white/10">
                      {/* Auth-aware thumbnail — fetch as blob */}
                      <FileThumb fileId={f.id} />
                    </div>
                  ) : (
                    <Icon className="h-5 w-5 text-white/70 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white/90 truncate">
                      {f.filename}
                    </div>
                    <div className="text-[11px] text-white/45 flex items-center gap-2">
                      <span>{humanSize(f.size)}</span>
                      {f.has_text && (
                        <>
                          <span>·</span>
                          <span className="text-blue-300/70">searchable</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            className="text-white/60 hover:text-white text-sm"
            data-testid="file-picker-cancel"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small helper that fetches an image file as a blob so we can render a
// thumbnail (auth headers can't be passed via <img src>).
const FileThumb = ({ fileId }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    filesApi
      .fetchBlobUrl(fileId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        blobUrl = u;
        setUrl(u);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]);
  if (!url)
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="h-3 w-3 animate-spin text-white/40" />
      </div>
    );
  return <img src={url} alt="" className="h-full w-full object-cover" />;
};
